// src/pages/PartsStepMobile.jsx
import React, { useEffect, useMemo } from "react";
import { motion, useMotionValue, animate } from "framer-motion";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import SelectableCard from "./SelectableCard"; // default, but can be overridden by prop
import PartSuggestionForm from "./PartSuggestionForm";

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};
const cardVariants = {
  hidden: { opacity: 0, y: 30 },
  show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } },
};

// swipe tuning
const SPRING = { type: "spring", stiffness: 420, damping: 40, mass: 0.6 };
const DIST_THRESHOLD = 60;   // px
const VEL_THRESHOLD = 300;  // px/s

export default function PartsStepMobile({
  items,
  currentPage,
  totalPages,
  canPrev,
  canNext,
  setCurrentPage,
  onSwipeEnd,
  onPartSelect,
  onSuggest,
  isMobile,
  showSuggest = true,

  // allow a custom card and extra props
  CardComponent = SelectableCard,
  cardPropsGetter, // (item) => ({ ...extraPropsForCard })
  setCurrentView

}) {
  const safeItems = useMemo(() => (Array.isArray(items) ? items : []), [items]);
  const x = useMotionValue(0);

  // whenever page changes via arrows/programmatically, snap the strip back to center
  useEffect(() => {
    animate(x, 0, SPRING);
  }, [currentPage, x]);

  const handleDragEnd = (evt, info) => {
    const { offset, velocity } = info;
    const dx = offset.x;
    const vx = velocity.x;

    let next = currentPage;

    // swipe left → go next
    if ((dx < -DIST_THRESHOLD || vx < -VEL_THRESHOLD) && canNext) {
      next = Math.min(totalPages - 1, currentPage + 1);
    }
    // swipe right → go prev
    if ((dx > DIST_THRESHOLD || vx > VEL_THRESHOLD) && canPrev) {
      next = Math.max(0, currentPage - 1);
    }

    if (next !== currentPage) setCurrentPage(next);

    // always re-center after deciding
    animate(x, 0, SPRING);

    // keep your original callback behavior
    if (onSwipeEnd) onSwipeEnd(evt, info);
  };

  return (
    <div className="parts-matrix-mobile">
      <motion.div
        className="parts-grid"
        variants={containerVariants}
        initial="hidden"
        animate="show"
        drag="x"
        style={{ x, touchAction: "pan-y" }}
        dragElastic={0.2}
        dragMomentum={false}
        onDragEnd={handleDragEnd}
        dir="rtl"
      >
        {safeItems.map((item) => (
          <motion.div key={item.id} variants={cardVariants} className="parts-grid-item">
            <CardComponent
              item={item}
              onClick={onPartSelect}
              isMobile={isMobile}
              {...(cardPropsGetter?.(item) || {})}
              setCurrentView = {setCurrentView}
            />
          </motion.div>
        ))}
      </motion.div>

      <div className="matrix-pager parts-pager">
        <button
          className="matrix-pager-btn"
          onClick={() => canNext && setCurrentPage((p) => Math.min(totalPages - 1, p + 1))}
          disabled={!canNext}
          aria-label="التالي"
        >
          <FiChevronRight size={28} />
        </button>

        <span className="matrix-pager-label">
          {totalPages} / {currentPage + 1}
        </span>

        <button
          className="matrix-pager-btn"
          onClick={() => canPrev && setCurrentPage((p) => Math.max(0, p - 1))}
          disabled={!canPrev}
          aria-label="السابق"
        >
          <FiChevronLeft size={28} />
        </button>
      </div>

      {/* Suggestion area can be omitted for favorites by passing showSuggest={false} */}
      {showSuggest && typeof onSuggest === "function" && (
        <PartSuggestionForm onSubmit={onSuggest} />
      )}
    </div>
  );
}
